import {
  Project,
  Identifiable,
  List,
  list,
  RetrieveProjectsFilter,
  Sort,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";
import { LinkedProject, Linker } from "../linkers/types";

type Response = {
  projects: (LinkedProject & {
    activityRoot: Identifiable<List>;
    _links: {
      [key: string]: { href: string };
    };
  })[];
};

function getProjects(httpClient: HttpClient, linker: Linker) {
  return (
    filter: RetrieveProjectsFilter,
    sort: Sort,
    size: number,
    index: number,
  ) => {
    const sortMap: { [key in Sort]: string[] } = {
      ascendingName: ["ascending", "name"],
      ascendingStartDate: ["ascending", "start"],
      ascendingEndDate: ["ascending", "end"],
      ascendingCompletionDate: ["ascending", "completionDate"],
      ascendingCreationDate: ["ascending", "creation"],
      ascendingRank: ["ascending", "rank"],
      descendingName: ["descending", "name"],
      descendingStartDate: ["descending", "start"],
      descendingEndDate: ["descending", "end"],
      descendingCompletionDate: ["descending", "completionDate"],
      descendingCreationDate: ["descending", "creation"],
      descendingRank: ["descending", "rank"],
      ascendingAuthor: ["", ""],
      descendingAuthor: ["", ""],
    };

    let sortType = "";
    let sortOrder = "";
    let customCriteriaUuid: string | null = null;

    if (sort.startsWith("customCriteria:")) {
      const parts = sort.split(":");
      customCriteriaUuid = parts[1];
      sortOrder = parts[2] === "asc" ? "ascending" : "descending";
    } else {
      const mapping =
        sortMap[sort as keyof typeof sortMap] || sortMap["ascendingName"];
      sortOrder = mapping[0];
      sortType = mapping[1];
    }

    const includedOrganizationalUnits = (filter.organizationalUnits ?? [])
      .filter(({ isExcluded }) => !isExcluded)
      .map(({ uuid }) => uuid);
    const excludedOrganizationalUnits = (filter.organizationalUnits ?? [])
      .filter(({ isExcluded }) => isExcluded)
      .map(({ uuid }) => uuid);

    return httpClient
      .sendHttpRequest<Response>({
        endpoint: `/projects/get-filtered-and-sorted-list`,
        method: HttpMethods.POST,
        data: {
          sortType,
          sortOrder,
          customCriteriaUuid,
          zones: filter.zones ? filter.zones.map(({ uuid }) => uuid) : [],
          statuses: filter.projectStatuses
            ? filter.projectStatuses.map(({ uuid }) => uuid)
            : [],
          projects: filter.projects
            ? filter.projects.map(({ uuid }) => uuid)
            : [],
          tags: filter.projectsTags
            ? filter.projectsTags.map(({ name }) => name)
            : [],
          resources: filter.resources
            ? filter.resources.map(({ uuid }) => uuid)
            : [],
          roles: filter.roles ? filter.roles.map(({ uuid }) => uuid) : [],
          organizationalUnits: includedOrganizationalUnits,
          excludedOrganizationalUnits: includedOrganizationalUnits.length
            ? excludedOrganizationalUnits
            : [],
          includeAllProjects: filter.includeAllProjects ?? false,
          options: {
            type: "details",
            size,
            page: index,
          },
        },
      })
      .then(({ projects }) => projects)
      .then(
        (
          promises: (Identifiable<LinkedProject> & {
            activityRoot: Identifiable<List>;
          })[],
        ) => Promise.all(promises),
      )
      .then((projects) =>
        projects.map((project) => ({
          ...project,
          rootList: { ...list, ...project.activityRoot },
          isTemplate: project.isTemplate || project.template,
          tags: project.tags
            ? (project.tags as any).map((name: string) => ({ name }))
            : [],
        })),
      )
      .catch((e) => Promise.reject({ ...e, message: e.details || "" }));
  };
}

export { getProjects };
